import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { GOLD, MUTED, BORDER, GREEN, RED, TEXT } from '../constants';
import { ITEM_CATEGORIES, METAL_TYPES, CHANNELS, DIRECTIONS } from '../constants';
import { today, uid, fmt, fmtDec } from '../utils';
import { runOCR, parseInvoiceText } from '../utils/ocr';
import { FormField, Input, Select, PrimaryBtn, GhostBtn, DangerBtn, SectionTitle, Card, Badge, EmptyState } from './UI';

const defaultForm = () => ({ date: today(), invoiceNumber: '', vendor: '', direction: 'Purchase', channel: 'Retail', notes: '' });
const defaultItem = () => ({ id: uid(), category: ITEM_CATEGORIES[0], description: '', metal: 'Gold', qty: 1, unitPrice: '', weight: '' });

export default function InvoicesTab({ invoices, onAdd }) {
  const [showForm,    setShowForm]    = useState(false);
  const [form,        setForm]        = useState(defaultForm());
  const [items,       setItems]       = useState([defaultItem()]);
  const [expanded,    setExpanded]    = useState(null);
  const [droppedFile, setDroppedFile] = useState(null);
  const [previewUrl,  setPreviewUrl]  = useState(null);
  const [ocrState,    setOcrState]    = useState('idle'); // idle | running | done | error
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrMessage,  setOcrMessage]  = useState('');
  const [rawOcrText,  setRawOcrText]  = useState('');
  const [showRaw,     setShowRaw]     = useState(false);

  function setF(k, v) { setForm(p => ({ ...p, [k]: v })); }
  function updateItem(id, k, v) { setItems(p => p.map(i => i.id === id ? { ...i, [k]: v } : i)); }

  async function processFile(file) {
    setDroppedFile(file);
    setShowForm(true);
    setOcrState('running');
    setOcrProgress(0);
    setOcrMessage('Loading file…');
    if (file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }
    try {
      const rawText = await runOCR(file, (pct, msg) => {
        setOcrProgress(pct);
        setOcrMessage(msg);
      });
      setRawOcrText(rawText);
      setOcrProgress(100);
      setOcrMessage('Parsing invoice…');
      const parsed = parseInvoiceText(rawText);
      setForm(p => ({
        ...p,
        invoiceNumber: parsed.invoiceNumber || p.invoiceNumber,
        vendor:        parsed.vendor        || p.vendor,
        date:          parsed.date          || p.date,
      }));
      if (parsed.lineItems.length > 0) {
        setItems(parsed.lineItems.map(li => ({
          id:          uid(),
          category:    li.category    || ITEM_CATEGORIES[0],
          description: li.description || '',
          metal:       li.metal       || 'Gold',
          qty:         li.qty         || 1,
          unitPrice:   li.unitPrice   || '',
          weight:      li.weight      || '',
        })));
      }
      setOcrState('done');
      setOcrMessage(`Found ${parsed.lineItems.length} line item${parsed.lineItems.length !== 1 ? 's' : ''} — review and correct before saving`);
    } catch (err) {
      console.error('OCR error:', err);
      setOcrState('error');
      setOcrMessage('OCR could not parse this file — please enter items manually below');
    }
  }

  const onDrop = useCallback((files) => { if (files[0]) processFile(files[0]); }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': [], 'image/*': [] },
    maxFiles: 1,
    disabled: showForm,
  });

  function handleSubmit(e) {
    e.preventDefault();
    const lineItems = items.filter(i => i.unitPrice).map(i => ({
      ...i,
      qty:       parseFloat(i.qty)       || 1,
      unitPrice: parseFloat(i.unitPrice) || 0,
      weight:    parseFloat(i.weight)    || 0,
    }));
    if (lineItems.length === 0) return;
    const total = lineItems.reduce((s, i) => s + i.qty * i.unitPrice, 0);
    onAdd({ ...form, lineItems, total, fileName: droppedFile?.name || null });
    resetForm();
  }

  function resetForm() {
    setForm(defaultForm());
    setItems([defaultItem()]);
    setDroppedFile(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setShowForm(false);
    setOcrState('idle');
    setOcrProgress(0);
    setOcrMessage('');
    setRawOcrText('');
    setShowRaw(false);
  }

  const total = items.reduce((s, i) => s + (parseFloat(i.qty)||1)*(parseFloat(i.unitPrice)||0), 0);

  const ocrColor = ocrState === 'error' ? RED : ocrState === 'done' ? GREEN : GOLD;

  return (
    <div>
      <SectionTitle
        title="Invoices"
        sub={`${invoices.length} invoices · Drop a PDF or image to auto-populate via OCR`}
        action={!showForm && <PrimaryBtn onClick={() => setShowForm(true)}>+ Enter Manually</PrimaryBtn>}
      />

      {/* DROP ZONE */}
      {!showForm && (
        <div {...getRootProps()} style={{
          border: `2px dashed ${isDragActive ? GOLD : BORDER}`,
          borderRadius: 12, padding: '52px 24px', textAlign: 'center',
          cursor: 'pointer', marginBottom: 24, transition: 'all 0.2s',
          background: isDragActive ? '#16130A' : 'transparent',
        }}>
          <input {...getInputProps()} />
          <div style={{ fontSize: 40, marginBottom: 14 }}>📄</div>
          <div style={{ color: isDragActive ? GOLD : TEXT, fontSize: 15, marginBottom: 8 }}>
            {isDragActive ? 'Drop to scan with OCR' : 'Drop an invoice here to auto-populate with OCR'}
          </div>
          <div style={{ color: MUTED, fontSize: 11, marginBottom: 18 }}>
            PDF · PNG · JPG — OCR runs entirely in your browser, nothing is uploaded
          </div>
          <GhostBtn style={{ pointerEvents: 'none' }}>Browse Files</GhostBtn>
        </div>
      )}

      {/* ENTRY FORM */}
      {showForm && (
        <Card style={{ padding: 24, marginBottom: 24 }}>

          {/* Top: preview + header fields */}
          <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>

            {/* Preview panel */}
            {(previewUrl || (droppedFile && !previewUrl)) && (
              <div style={{ flexShrink: 0, width: 200 }}>
                <div style={{ fontSize: 9, color: MUTED, letterSpacing: 1.5, marginBottom: 8 }}>
                  {previewUrl ? 'INVOICE PREVIEW' : 'FILE ATTACHED'}
                </div>
                {previewUrl ? (
                  <img src={previewUrl} alt="Invoice" style={{
                    width: '100%', borderRadius: 6, border: `1px solid ${BORDER}`,
                    maxHeight: 280, objectFit: 'contain', background: '#fff',
                  }} />
                ) : (
                  <div style={{ background: '#0D1117', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '24px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>📄</div>
                    <div style={{ color: MUTED, fontSize: 10, wordBreak: 'break-all' }}>{droppedFile.name}</div>
                    <div style={{ color: MUTED, fontSize: 9, marginTop: 4 }}>{(droppedFile.size/1024).toFixed(0)} KB</div>
                  </div>
                )}
              </div>
            )}

            <div style={{ flex: 1 }}>
              {/* File/title bar */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div style={{ fontSize: 12, color: GOLD, letterSpacing: 1 }}>
                  {droppedFile ? `📎 ${droppedFile.name}` : 'NEW INVOICE'}
                </div>
                {!droppedFile && (
                  <label style={{ cursor: 'pointer' }}>
                    <GhostBtn style={{ fontSize: 11 }} onClick={e => e.stopPropagation()}>📎 Attach & OCR</GhostBtn>
                    <input type="file" accept="image/*,.pdf" style={{ display: 'none' }}
                      onChange={e => { if (e.target.files[0]) processFile(e.target.files[0]); }} />
                  </label>
                )}
              </div>

              {/* OCR progress / status */}
              {ocrState !== 'idle' && (
                <div style={{ marginBottom: 16, background: '#0D1117', border: `1px solid ${ocrColor}40`, borderRadius: 8, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: ocrState === 'running' ? 8 : 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {ocrState === 'running' && <Spinner />}
                      {ocrState === 'done'    && <span style={{ color: GREEN, fontSize: 14 }}>✓</span>}
                      {ocrState === 'error'   && <span style={{ color: RED,   fontSize: 14 }}>✕</span>}
                      <span style={{ color: ocrColor, fontSize: 12 }}>{ocrMessage}</span>
                    </div>
                    {rawOcrText && (
                      <GhostBtn onClick={() => setShowRaw(!showRaw)} style={{ fontSize: 10, padding: '3px 8px' }}>
                        {showRaw ? 'Hide' : 'Raw'} OCR text
                      </GhostBtn>
                    )}
                  </div>
                  {ocrState === 'running' && (
                    <div style={{ background: BORDER, borderRadius: 4, height: 4 }}>
                      <div style={{ background: GOLD, height: 4, borderRadius: 4, width: `${ocrProgress}%`, transition: 'width 0.3s' }} />
                    </div>
                  )}
                  {showRaw && rawOcrText && (
                    <pre style={{
                      marginTop: 10, background: '#060810', border: `1px solid ${BORDER}`,
                      borderRadius: 6, padding: 10, fontSize: 9, color: MUTED,
                      whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto',
                      fontFamily: "'DM Mono', monospace",
                    }}>{rawOcrText}</pre>
                  )}
                </div>
              )}

              {/* Header fields */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <FormField label="Invoice #" minWidth={130}>
                  <Input value={form.invoiceNumber} onChange={e=>setF('invoiceNumber',e.target.value)} placeholder="INV-001" required />
                </FormField>
                <FormField label="Date" minWidth={130}>
                  <Input type="date" value={form.date} onChange={e=>setF('date',e.target.value)} />
                </FormField>
                <FormField label="Direction" minWidth={108}>
                  <Select value={form.direction} onChange={e=>setF('direction',e.target.value)}>
                    {DIRECTIONS.map(d=><option key={d}>{d}</option>)}
                  </Select>
                </FormField>
                <FormField label="Channel" minWidth={108}>
                  <Select value={form.channel} onChange={e=>setF('channel',e.target.value)}>
                    {CHANNELS.map(c=><option key={c}>{c}</option>)}
                  </Select>
                </FormField>
                <FormField label="Vendor / Customer" flex={2} minWidth={180}>
                  <Input value={form.vendor} onChange={e=>setF('vendor',e.target.value)} placeholder="Company or name" />
                </FormField>
              </div>
            </div>
          </div>

          {/* LINE ITEMS */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontSize: 10, color: MUTED, letterSpacing: 1.5 }}>
              LINE ITEMS
              {ocrState === 'done' && items.length > 0 && (
                <span style={{ color: GREEN, marginLeft: 10 }}>✓ OCR populated — review and edit as needed</span>
              )}
            </div>
          </div>

          {ocrState === 'running' ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: MUTED }}>
              <Spinner size={28} /><br /><br />
              <span style={{ fontSize: 12 }}>Scanning… keep this tab open</span>
            </div>
          ) : (
            <>
              {items.map((item, idx) => (
                <div key={item.id} style={{ background: '#0D1117', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                  <div style={{ fontSize: 9, color: MUTED, letterSpacing: 1, marginBottom: 8 }}>ITEM #{idx+1}</div>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <FormField label="Category" flex={2} minWidth={140}>
                      <Select value={item.category} onChange={e=>updateItem(item.id,'category',e.target.value)} style={{ fontSize: 11 }}>
                        {ITEM_CATEGORIES.map(c=><option key={c}>{c}</option>)}
                      </Select>
                    </FormField>
                    <FormField label="Description" flex={4} minWidth={220}>
                      <Input value={item.description} onChange={e=>updateItem(item.id,'description',e.target.value)} placeholder="Description" style={{ fontSize: 11 }} />
                    </FormField>
                    <FormField label="Metal" minWidth={100}>
                      <Select value={item.metal} onChange={e=>updateItem(item.id,'metal',e.target.value)} style={{ fontSize: 11 }}>
                        {METAL_TYPES.map(m=><option key={m}>{m}</option>)}
                      </Select>
                    </FormField>
                    <FormField label="Qty" flex={0} minWidth={65}>
                      <Input type="number" min="0.001" step="any" value={item.qty} onChange={e=>updateItem(item.id,'qty',e.target.value)} style={{ fontSize: 11, width: 70 }} />
                    </FormField>
                    <FormField label="Unit Price $" flex={0} minWidth={95}>
                      <Input type="number" step="0.01" value={item.unitPrice} onChange={e=>updateItem(item.id,'unitPrice',e.target.value)} placeholder="0.00" style={{ fontSize: 11, width: 100 }} />
                    </FormField>
                    <FormField label="Weight oz" flex={0} minWidth={80}>
                      <Input type="number" step="0.0001" value={item.weight} onChange={e=>updateItem(item.id,'weight',e.target.value)} placeholder="0" style={{ fontSize: 11, width: 80 }} />
                    </FormField>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 2 }}>
                      <span style={{ color: GOLD, fontSize: 14, minWidth: 78 }}>
                        {item.qty && item.unitPrice ? fmt((parseFloat(item.qty)||1)*(parseFloat(item.unitPrice)||0)) : '—'}
                      </span>
                      {items.length > 1 && <DangerBtn onClick={()=>setItems(p=>p.filter(i=>i.id!==item.id))}>✕</DangerBtn>}
                    </div>
                  </div>
                </div>
              ))}

              <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 16 }}>
                <GhostBtn onClick={()=>setItems(p=>[...p,defaultItem()])}>+ Add Line Item</GhostBtn>
                <span style={{ color: GOLD, fontSize: 16, fontWeight: 500 }}>Total: {fmt(total)}</span>
              </div>

              <FormField label="Notes" style={{ marginBottom: 20 }}>
                <Input value={form.notes} onChange={e=>setF('notes',e.target.value)} placeholder="Optional notes" />
              </FormField>

              <div style={{ display: 'flex', gap: 12 }}>
                <PrimaryBtn onClick={handleSubmit}>Save Invoice & Post Transactions</PrimaryBtn>
                <GhostBtn onClick={resetForm}>Cancel</GhostBtn>
              </div>
            </>
          )}
        </Card>
      )}

      {/* INVOICE LIST */}
      {invoices.length === 0 ? (
        <EmptyState text="No invoices yet — drop a PDF or image above to scan with OCR" icon="📋" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {invoices.map((inv) => (
            <Card key={inv.id} style={{ padding: '16px 20px' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', cursor: 'pointer' }}
                onClick={() => setExpanded(expanded === inv.id ? null : inv.id)}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <span style={{ color: GOLD, fontWeight: 500, fontSize: 13 }}>{inv.invoiceNumber || '—'}</span>
                  <span style={{ color: MUTED, fontSize: 11 }}>{inv.date}</span>
                  <Badge color={inv.direction === 'Sale' ? GREEN : RED}>{inv.direction}</Badge>
                  <Badge>{inv.channel}</Badge>
                  {inv.fileName && <span style={{ color: MUTED, fontSize: 10 }}>📎 {inv.fileName}</span>}
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexShrink: 0, marginLeft: 16 }}>
                  <span style={{ color: TEXT, fontWeight: 500, fontSize: 15 }}>{fmt(inv.total)}</span>
                  <span style={{ color: MUTED, fontSize: 10 }}>{expanded === inv.id ? '▲' : '▼'}</span>
                </div>
              </div>
              <div style={{ color: MUTED, fontSize: 11, marginTop: 4 }}>{inv.vendor} · {inv.lineItems?.length || 0} line items</div>

              {expanded === inv.id && inv.lineItems && (
                <div style={{ marginTop: 14, borderTop: `1px solid ${BORDER}`, paddingTop: 14 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr>
                        {['Category','Description','Metal','Qty','Unit Price','Weight','Line Total'].map(h => (
                          <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontSize: 9, color: MUTED, letterSpacing: 1, borderBottom: `1px solid ${BORDER}` }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {inv.lineItems.map((li, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid #161D28` }}>
                          <td style={{ padding: '7px 10px' }}>{li.category}</td>
                          <td style={{ padding: '7px 10px' }}>{li.description}</td>
                          <td style={{ padding: '7px 10px', color: MUTED }}>{li.metal}</td>
                          <td style={{ padding: '7px 10px' }}>{li.qty}</td>
                          <td style={{ padding: '7px 10px' }}>{fmtDec(li.unitPrice)}</td>
                          <td style={{ padding: '7px 10px', color: MUTED }}>{li.weight ? `${li.weight}oz` : '—'}</td>
                          <td style={{ padding: '7px 10px', color: GOLD, fontWeight: 500 }}>{fmt(li.qty * li.unitPrice)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {inv.notes && <div style={{ color: MUTED, fontSize: 11, marginTop: 10 }}>Notes: {inv.notes}</div>}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Spinner({ size = 14 }) {
  return (
    <span style={{
      display: 'inline-block', width: size, height: size,
      border: `2px solid #333`, borderTop: `2px solid ${GOLD}`,
      borderRadius: '50%', animation: 'agc-spin 0.8s linear infinite',
    }} />
  );
}

if (typeof document !== 'undefined' && !document.getElementById('agc-spin')) {
  const s = document.createElement('style');
  s.id = 'agc-spin';
  s.textContent = '@keyframes agc-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}
